import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import PaymentSheet from '../components/sales/PaymentSheet';
import PaymentRow from '../components/sales/PaymentRow';
import TotalsCard from '../components/sales/TotalsCard';
import BalanceCard from '../components/sales/BalanceCard';
import { useSales } from '../hooks/useSales';
import { computeSale, priceOf, ILS, formatRelativeDate, formatFullDateTime, buildWhatsAppText, getSaleDogs } from '../utils/salesUtils';
import { CATALOG } from '../constants/catalog';
import '../styles/pages/sales.css';

const STATUS_LABELS = { paid: 'Pagado', partial: 'Parcial', due: 'Pendiente', draft: 'Borrador', cancelled: 'Cancelada' };

const AVATAR_COLORS = ['dog', 'dog2', 'dog3', 'dog4', 'dog5'];
const avatarColor = (str) => AVATAR_COLORS[(str?.charCodeAt?.(0) || 0) % AVATAR_COLORS.length];

export default function SaleDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { allSales, addPayment, deletePayment, cancelSale, getSaleById } = useSales();

  const [fetchedSale, setFetchedSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoadingSale(true);
    if (id) {
      getSaleById(id).then(data => {
        if (isMounted && data) setFetchedSale(data);
        if (isMounted) setLoadingSale(false);
      });
    } else {
      setLoadingSale(false);
    }
    return () => { isMounted = false; };
  }, [id, getSaleById]);

  const sale = fetchedSale || allSales.find(s => s._id === id || s.id === id);

  const [sheet, setSheet] = useState(null);
  const [toast, setToast] = useState('');
  const [localPayments, setLocalPayments] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const payments = localPayments ?? sale?.payments ?? [];

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  const { subtotal, discountAmount, total, remaining, status } = useMemo(
    () => computeSale(sale?.items || [], sale?.discount ?? null, payments),
    [sale, payments]
  );

  const handleAddPayment = async (p) => {
    setSheet(null);
    if (!sale) return;
    const saleId = sale._id || sale.id;
    const updated = await addPayment(saleId, p);
    if (updated?.payments) setLocalPayments(updated.payments);
    showToast('Pago registrado');
  };

  const handleCancel = async () => {
    if (!sale) return;
    if (!window.confirm('¿Cancelar esta venta? Esta acción no se puede deshacer.')) return;
    setCancelling(true);
    const updated = await cancelSale(sale._id || sale.id);
    setCancelling(false);
    if (updated) showToast('Venta cancelada');
  };

  const handleDeletePayment = async (paymentIdx) => {
    if (!sale) return;
    const saleId = sale._id || sale.id;
    const payment = payments[paymentIdx];
    const paymentId = payment?._id || payment?.id;

    // Optimistic remove
    const next = payments.filter((_, i) => i !== paymentIdx);
    setLocalPayments(next);

    if (paymentId) {
      const updated = await deletePayment(saleId, paymentId, s => ({
        ...s,
        payments: (s.payments || []).filter((_, i) => i !== paymentIdx),
      }));
      if (updated?.payments) setLocalPayments(updated.payments);
    }
  };

  if (loadingSale && !sale) {
    return (
      <div className="pos-page pos-scope">
        <Navigation />
        <div className="pos-topbar">
          <button className="pos-back-btn" onClick={() => navigate('/pos')}>‹</button>
          <h1>Venta</h1>
          <div style={{ width: 38 }} />
        </div>
        <div className="pos-hint" style={{ marginTop: 40 }}>Cargando venta…</div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="pos-page pos-scope">
        <Navigation />
        <div className="pos-topbar">
          <button className="pos-back-btn" onClick={() => navigate('/pos')}>‹</button>
          <h1>Venta</h1>
          <div style={{ width: 38 }} />
        </div>
        <div className="pos-hint" style={{ marginTop: 40 }}>Venta no encontrada.</div>
      </div>
    );
  }

  const saleDogs = getSaleDogs(sale);
  const dogName = saleDogs.length > 0 ? saleDogs.map(d => d.name).join(' & ') : 'Público';
  const ownerName = saleDogs[0]?.owners?.[0]?.name || '';
  const color = avatarColor(saleDogs[0]?._id || saleDogs[0]?.id || dogName);
  const whenStr = sale.createdAt ? formatRelativeDate(sale.createdAt) : '';
  const isCancelled = status === 'cancelled' || sale.status === 'cancelled';
  const statusLabel = STATUS_LABELS[sale.status] || STATUS_LABELS[status] || status;
  const saleTitle = sale.saleNumber ? `#${String(sale.saleNumber).padStart(4, '0')}` : `#${(sale._id || sale.id)?.slice(-6).toUpperCase()}`;

  const createdByName = sale.createdBy?.username || sale.createdBy?.name || '';
  const updatedByName = sale.updatedBy?.username || sale.updatedBy?.name || '';
  const showUpdated = updatedByName && updatedByName !== createdByName;

  return (
    <div className="pos-page pos-scope">
      <Navigation />

      {/* Top bar */}
      <div className="pos-topbar">
        <button className="pos-back-btn" onClick={() => navigate('/pos')}>‹</button>
        <h1>Venta {saleTitle}</h1>
        <div style={{ width: 38 }} />
      </div>

      {/* Customer card (read-only) */}
      <div className="pos-detail-customer">
        <div className="pos-customer-row">
          <div className={`pos-customer-avatar ${color}`} style={{ width: 48, height: 48, fontSize: 24 }}>🐕</div>
          <div style={{ flex: 1 }}>
            <div className="pos-customer-dog-name">{dogName}</div>
            <div className="pos-detail-meta">
              {whenStr}{ownerName ? ` · ${ownerName}` : ''}
            </div>
          </div>
          <span className={`pos-pill pos-pill-${isCancelled ? 'cancelled' : status}`}>
            {isCancelled ? 'Cancelada' : status === 'partial' ? `${ILS(remaining)} pend.` : statusLabel}
          </span>
        </div>

        {/* Audit row */}
        {createdByName && (
          <div className="pos-audit-row">
            <span>Creado por <strong>{createdByName}</strong>{sale.createdAt ? ` · ${formatFullDateTime(sale.createdAt)}` : ''}</span>
            {showUpdated && (
              <span><br />Editado por <strong>{updatedByName}</strong>{sale.updatedAt ? ` · ${formatFullDateTime(sale.updatedAt)}` : ''}</span>
            )}
          </div>
        )}
      </div>

      {/* Items (read-only) */}
      <div className="pos-section"><h2>Ítems</h2></div>
      <div className="pos-item-list">
        {(sale.items || []).map((it, i) => {
          const unitPrice = priceOf(it);
          const catDef = CATALOG[it.cat];
          const catIcon = catDef?.icon || '🛍️';
          return (
            <div key={i} className="pos-ro-line-item">
              <div style={{ fontSize: 20, flexShrink: 0 }}>{catIcon}</div>
              <div className="pos-ro-name">
                {it.name}
                <span className="pos-ro-sub">
                  {it.qty || 1} × {ILS(unitPrice)}
                  {it.sized ? ` · ${it.size}` : ''}
                </span>
              </div>
              <span className="pos-ro-total">{ILS(unitPrice * (it.qty || 1))}</span>
            </div>
          );
        })}
      </div>

      {/* Internal Note (Staff only) */}
      {sale?.internalNote && (
        <>
          <div className="pos-section"><h2>Nota interna</h2></div>
          <div className="pos-note-card" style={{ background: '#f8fafc', borderColor: '#cbd5e1' }}>
            <div className="pos-note-header">
              <span className="pos-note-tag">🔒 Solo personal</span>
            </div>
            <p style={{ margin: 0, fontSize: 13.5, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
              {sale.internalNote}
            </p>
          </div>
        </>
      )}

      {/* Totals */}
      <div className="pos-section"><h2>Total</h2></div>
      <TotalsCard
        subtotal={subtotal}
        discountAmount={discountAmount}
        discount={sale.discount}
        total={total}
      />

      {/* Payments */}
      <div className="pos-section">
        <h2>Pagos · {payments.length}</h2>
        {!isCancelled && remaining > 0 && (
          <button className="pos-section-action" onClick={() => setSheet('pay')}>+ Registrar</button>
        )}
      </div>
      <div className="pos-payments-wrap">
        {payments.map((p, i) => (
          <PaymentRow
            key={p._id || i}
            payment={p}
            fallbackUser={!p.recordedBy ? (updatedByName || createdByName) : undefined}
            onDelete={!isCancelled ? () => handleDeletePayment(i) : undefined}
          />
        ))}
        {payments.length === 0 && !isCancelled && (
          <button className="pos-add-item-btn" onClick={() => setSheet('pay')}>
            <span style={{ fontSize: 18 }}>＋</span> Registrar primer pago
          </button>
        )}
        {!isCancelled && (
          <BalanceCard
            remaining={remaining}
            total={total}
            onPayRest={remaining > 0 ? () => setSheet('pay') : null}
          />
        )}
      </div>

      {/* Sticky bottom actions */}
      <div className="pos-sticky-cta">
        <div className="pos-cta-btns">
          {!isCancelled && (
            <>
              <button className="pos-cta-ghost" onClick={() => navigate(`/pos/${id}/edit`)}>
                Editar ítems
              </button>
              <button className="pos-cta-btn" onClick={() => window.open(`/pos/${id}/receipt`, '_blank')}>
                🖨️ Imprimir
              </button>
              <button
                className="pos-cta-ghost"
                style={{ background: '#25D366', color: '#fff', borderColor: '#25D366' }}
                onClick={() => {
                  const text = buildWhatsAppText(sale, payments, total, remaining);
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                }}
              >
                💬 WhatsApp
              </button>
            </>
          )}
          {!isCancelled && (
            <button
              className="pos-cta-cancel"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelando…' : 'Cancelar venta'}
            </button>
          )}
        </div>
      </div>

      {sheet === 'pay' && (
        <PaymentSheet
          remaining={remaining}
          onClose={() => setSheet(null)}
          onSave={handleAddPayment}
        />
      )}

      {toast && <div className="pos-toast">{toast}</div>}
    </div>
  );
}
